/**
 * ConnectionOverlay - Connection loss overlay for Poker Reborn
 *
 * Shows reconnection status during connection loss with:
 * - Reconnecting state with countdown timer and attempt counter
 * - Reconnected state with success animation
 * - Failed state with diagnostic message and retry button
 *
 * @see tasks/active/T09_CONNECTION_LOSS_HANDLING_PLAN.md
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Button, SECONDARY_BUTTON_STYLE } from '../Button';
import type { ConnectionDiagnostic } from '@/types/network';

// =============================================================================
// TYPES
// =============================================================================

type OverlayState = 'hidden' | 'reconnecting' | 'reconnected' | 'failed';

// =============================================================================
// VISUAL CONSTANTS
// =============================================================================

const OVERLAY = {
  // Background
  backgroundColor: 0x000000,
  backgroundAlpha: 0.5,

  // Dialog
  dialogBg: 0x1a1a2e,
  dialogBorder: 0x3d3d5c,
  dialogBorderWidth: 2,
  dialogRadius: 16,
  dialogWidth: 400,
  dialogPadding: 32,

  // Progress bar
  progressWidth: 300,
  progressHeight: 8,
  progressBg: 0x333333,
  progressFill: 0x4fc3f7,
  progressRadius: 4,

  // Colors
  iconReconnecting: 0xffa726,
  iconReconnected: 0x66bb6a,
  iconFailed: 0xef5350,
  textTitle: 0xffffff,
  textSubtitle: 0xaaaaaa,
  textAttempt: 0x888888,
  textSeatReserved: 0x66bb6a,

  // Typography
  titleSize: 24,
  subtitleSize: 16,
  attemptSize: 14,
  seatSize: 14,

  // Animation
  appearDuration: 200,
  disappearDuration: 200,
  successHoldDuration: 1000,

  // Reconnect now button
  reconnectNowEnableDelay: 2000, // 2 seconds before button is enabled
};

// =============================================================================
// CONNECTION OVERLAY
// =============================================================================

export class ConnectionOverlay extends Container {
  // Background
  private background: Graphics;

  // Dialog container
  private dialog: Container;
  private dialogBg: Graphics;

  // Content elements
  private iconText: Text;
  private titleText: Text;
  private subtitleText: Text;
  private attemptText: Text;
  private progressBg: Graphics;
  private progressFill: Graphics;
  private countdownText: Text;
  private seatReservedText: Text;
  private retryButton: Button;
  private reconnectNowButton: Button;

  // State
  private state: OverlayState = 'hidden';
  private countdownMs = 0;
  private countdownStartMs = 0;
  private progressBarY = 0; // Y position of progress bar for animation updates
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private successTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectNowEnableTimer: ReturnType<typeof setTimeout> | null = null;
  private retryCallback: (() => void) | null = null;
  private reconnectNowCallback: (() => void) | null = null;

  // Screen dimensions for centering
  private screenWidth = 0;
  private screenHeight = 0;

  constructor() {
    super();

    this.visible = false;
    this.eventMode = 'static'; // Block clicks through overlay

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create dialog container
    this.dialog = new Container();
    this.addChild(this.dialog);

    // Dialog background
    this.dialogBg = new Graphics();
    this.dialog.addChild(this.dialogBg);

    // Icon (emoji text for now)
    this.iconText = new Text({
      text: '',
      style: {
        fontSize: 48,
        fill: OVERLAY.iconReconnecting,
      },
    });
    this.iconText.anchor.set(0.5, 0.5);
    this.dialog.addChild(this.iconText);

    // Title
    this.titleText = new Text({
      text: '',
      style: {
        fontSize: OVERLAY.titleSize,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
        fill: OVERLAY.textTitle,
      },
    });
    this.titleText.anchor.set(0.5, 0);
    this.dialog.addChild(this.titleText);

    // Subtitle
    this.subtitleText = new Text({
      text: '',
      style: {
        fontSize: OVERLAY.subtitleSize,
        fontFamily: 'Roboto, Arial, sans-serif',
        fill: OVERLAY.textSubtitle,
      },
    });
    this.subtitleText.anchor.set(0.5, 0);
    this.dialog.addChild(this.subtitleText);

    // Attempt text
    this.attemptText = new Text({
      text: '',
      style: {
        fontSize: OVERLAY.attemptSize,
        fontFamily: 'Roboto, Arial, sans-serif',
        fill: OVERLAY.textAttempt,
      },
    });
    this.attemptText.anchor.set(0.5, 0);
    this.dialog.addChild(this.attemptText);

    // Progress bar background
    this.progressBg = new Graphics();
    this.dialog.addChild(this.progressBg);

    // Progress bar fill
    this.progressFill = new Graphics();
    this.dialog.addChild(this.progressFill);

    // Countdown text
    this.countdownText = new Text({
      text: '',
      style: {
        fontSize: OVERLAY.attemptSize,
        fontFamily: 'Roboto, Arial, sans-serif',
        fill: OVERLAY.textAttempt,
      },
    });
    this.countdownText.anchor.set(0.5, 0);
    this.dialog.addChild(this.countdownText);

    // Seat reserved text
    this.seatReservedText = new Text({
      text: 'Your seat is reserved',
      style: {
        fontSize: OVERLAY.seatSize,
        fontFamily: 'Roboto, Arial, sans-serif',
        fill: OVERLAY.textSeatReserved,
      },
    });
    this.seatReservedText.anchor.set(0.5, 0);
    this.seatReservedText.visible = false;
    this.dialog.addChild(this.seatReservedText);

    // Retry button (for failed state)
    this.retryButton = new Button({
      label: 'Retry',
      width: 120,
      height: 40,
      style: SECONDARY_BUTTON_STYLE,
    });
    this.retryButton.visible = false;
    this.retryButton.onClick = () => {
      if (this.retryCallback) {
        this.retryCallback();
      }
    };
    this.dialog.addChild(this.retryButton);

    // Reconnect Now button (for reconnecting state)
    this.reconnectNowButton = new Button({
      label: 'Try Now',
      width: 140,
      height: 40,
      style: SECONDARY_BUTTON_STYLE,
    });
    this.reconnectNowButton.visible = false;
    this.reconnectNowButton.onClick = () => {
      if (this.reconnectNowCallback) {
        this.reconnectNowCallback();
      }
    };
    this.dialog.addChild(this.reconnectNowButton);
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Show the overlay with reconnecting state
   * @param attempt - Current reconnection attempt number
   * @param delayMs - Milliseconds until next attempt
   * @param onReconnectNow - Optional callback for "Try Now" button
   */
  showReconnecting(attempt: number, delayMs: number, onReconnectNow?: () => void): void {
    this.clearTimers();
    this.state = 'reconnecting';
    this.reconnectNowCallback = onReconnectNow ?? null;

    // Set icon
    this.iconText.text = '\u26A1'; // Lightning bolt
    this.iconText.style.fill = OVERLAY.iconReconnecting;

    // Set text
    this.titleText.text = 'Connection Lost';
    this.subtitleText.text = 'Reconnecting...';
    this.attemptText.text = 'Attempt ' + String(attempt);
    this.attemptText.visible = true;

    // Show progress bar
    this.progressBg.visible = true;
    this.progressFill.visible = true;
    this.countdownText.visible = true;

    // Hide retry button
    this.retryButton.visible = false;

    // Show "Try Now" button (disabled initially, enables after delay)
    if (onReconnectNow) {
      this.reconnectNowButton.visible = true;
      this.reconnectNowButton.disabled = true;
      this.startReconnectNowEnableTimer();
    } else {
      this.reconnectNowButton.visible = false;
    }

    // Start countdown
    this.countdownMs = delayMs;
    this.countdownStartMs = delayMs;
    this.startCountdown();

    // Layout and show
    this.layoutDialog();
    this.show();
  }

  /**
   * Show brief success state before hiding
   */
  showReconnected(): void {
    this.clearTimers();
    this.state = 'reconnected';

    // Set icon
    this.iconText.text = '\u2713'; // Checkmark
    this.iconText.style.fill = OVERLAY.iconReconnected;

    // Set text
    this.titleText.text = 'Connected';
    this.subtitleText.text = 'Resuming...';
    this.attemptText.visible = false;

    // Hide progress bar
    this.progressBg.visible = false;
    this.progressFill.visible = false;
    this.countdownText.visible = false;

    // Hide buttons
    this.retryButton.visible = false;
    this.reconnectNowButton.visible = false;

    // Layout and show
    this.layoutDialog();
    this.show();

    // Auto-hide after success duration
    this.successTimer = setTimeout(() => {
      this.hide();
    }, OVERLAY.successHoldDuration);
  }

  /**
   * Show failure state with diagnostic info
   * @param diagnostic - Error diagnostic from NetworkDiagnostics
   * @param onRetry - Callback when retry button clicked
   */
  showFailed(diagnostic: ConnectionDiagnostic, onRetry: () => void): void {
    this.clearTimers();
    this.state = 'failed';
    this.retryCallback = onRetry;

    // Set icon
    this.iconText.text = '\u2717'; // X mark
    this.iconText.style.fill = OVERLAY.iconFailed;

    // Set text
    this.titleText.text = 'Connection Failed';

    // Build subtitle from diagnostic
    let subtitle = diagnostic.message;
    if (diagnostic.suggestion) {
      subtitle += '\n' + diagnostic.suggestion;
    }
    this.subtitleText.text = subtitle;
    this.attemptText.visible = false;

    // Hide progress bar
    this.progressBg.visible = false;
    this.progressFill.visible = false;
    this.countdownText.visible = false;

    // Show retry button, hide reconnect now button
    this.retryButton.visible = true;
    this.reconnectNowButton.visible = false;

    // Layout and show
    this.layoutDialog();
    this.show();
  }

  /**
   * Hide the overlay
   */
  hide(): void {
    this.clearTimers();
    this.state = 'hidden';
    this.visible = false;
    this.retryCallback = null;
    this.reconnectNowCallback = null;
  }

  /**
   * Update the "seat reserved" message visibility
   * @param showSeatReserved - Whether player has a seat at risk
   */
  setSeatReservedVisible(showSeatReserved: boolean): void {
    this.seatReservedText.visible = showSeatReserved;
    this.layoutDialog();
  }

  /**
   * Update screen dimensions for centering
   */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    this.renderBackground();
    this.centerDialog();
  }

  /**
   * Get current overlay state
   */
  getState(): OverlayState {
    return this.state;
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private show(): void {
    this.visible = true;
    this.renderBackground();
    this.centerDialog();
  }

  private renderBackground(): void {
    this.background.clear();
    this.background.rect(0, 0, this.screenWidth, this.screenHeight);
    this.background.fill({ color: OVERLAY.backgroundColor, alpha: OVERLAY.backgroundAlpha });
  }

  private layoutDialog(): void {
    const padding = OVERLAY.dialogPadding;
    const width = OVERLAY.dialogWidth;
    let yOffset = padding;

    // Icon
    this.iconText.x = width / 2;
    this.iconText.y = yOffset + 24;
    yOffset += 60;

    // Title
    this.titleText.x = width / 2;
    this.titleText.y = yOffset;
    yOffset += this.titleText.height + 8;

    // Subtitle
    this.subtitleText.x = width / 2;
    this.subtitleText.y = yOffset;
    yOffset += this.subtitleText.height + 12;

    // Attempt text (if visible)
    if (this.attemptText.visible) {
      this.attemptText.x = width / 2;
      this.attemptText.y = yOffset;
      yOffset += this.attemptText.height + 16;
    }

    // Progress bar (if visible)
    if (this.progressBg.visible) {
      const progressX = (width - OVERLAY.progressWidth) / 2;
      this.progressBarY = yOffset; // Store for animation updates

      this.progressBg.clear();
      this.progressBg.roundRect(progressX, yOffset, OVERLAY.progressWidth, OVERLAY.progressHeight, OVERLAY.progressRadius);
      this.progressBg.fill(OVERLAY.progressBg);

      // Progress fill will be updated by countdown
      this.updateProgressFill(progressX, yOffset);

      yOffset += OVERLAY.progressHeight + 8;

      // Countdown text
      this.countdownText.x = width / 2;
      this.countdownText.y = yOffset;
      yOffset += this.countdownText.height + 16;
    }

    // Reconnect Now button (if visible, appears during reconnecting state)
    if (this.reconnectNowButton.visible) {
      this.reconnectNowButton.x = (width - this.reconnectNowButton.buttonWidth) / 2;
      this.reconnectNowButton.y = yOffset;
      yOffset += this.reconnectNowButton.buttonHeight + 16;
    }

    // Seat reserved text (if visible)
    if (this.seatReservedText.visible) {
      this.seatReservedText.x = width / 2;
      this.seatReservedText.y = yOffset;
      yOffset += this.seatReservedText.height + 16;
    }

    // Retry button (if visible)
    if (this.retryButton.visible) {
      this.retryButton.x = (width - this.retryButton.buttonWidth) / 2;
      this.retryButton.y = yOffset;
      yOffset += this.retryButton.buttonHeight + 16;
    }

    yOffset += padding - 16; // Final padding

    // Draw dialog background
    const height = yOffset;
    this.dialogBg.clear();
    this.dialogBg.roundRect(0, 0, width, height, OVERLAY.dialogRadius);
    this.dialogBg.fill(OVERLAY.dialogBg);
    this.dialogBg.roundRect(0, 0, width, height, OVERLAY.dialogRadius);
    this.dialogBg.stroke({ width: OVERLAY.dialogBorderWidth, color: OVERLAY.dialogBorder });

    // Update dialog pivot for centering
    this.dialog.pivot.set(width / 2, height / 2);
  }

  private centerDialog(): void {
    this.dialog.x = this.screenWidth / 2;
    this.dialog.y = this.screenHeight / 2;
  }

  private updateProgressFill(progressX: number, progressY: number): void {
    const progress = this.countdownStartMs > 0
      ? this.countdownMs / this.countdownStartMs
      : 0;
    const fillWidth = OVERLAY.progressWidth * progress;

    this.progressFill.clear();
    if (fillWidth > 0) {
      this.progressFill.roundRect(progressX, progressY, fillWidth, OVERLAY.progressHeight, OVERLAY.progressRadius);
      this.progressFill.fill(OVERLAY.progressFill);
    }
  }

  private startCountdown(): void {
    this.updateCountdownText();

    this.countdownTimer = setInterval(() => {
      this.countdownMs = Math.max(0, this.countdownMs - 100);
      this.updateCountdownText();

      // Update progress bar fill using stored Y position
      const progressX = (OVERLAY.dialogWidth - OVERLAY.progressWidth) / 2;
      this.updateProgressFill(progressX, this.progressBarY);
    }, 100);
  }

  private updateCountdownText(): void {
    const seconds = Math.ceil(this.countdownMs / 1000);
    this.countdownText.text = String(seconds) + 's';
  }

  private startReconnectNowEnableTimer(): void {
    // Clear any existing enable timer
    if (this.reconnectNowEnableTimer) {
      clearTimeout(this.reconnectNowEnableTimer);
    }

    // Enable button after delay
    this.reconnectNowEnableTimer = setTimeout(() => {
      this.reconnectNowButton.disabled = false;
      this.reconnectNowEnableTimer = null;
    }, OVERLAY.reconnectNowEnableDelay);
  }

  private clearTimers(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    if (this.successTimer) {
      clearTimeout(this.successTimer);
      this.successTimer = null;
    }
    if (this.reconnectNowEnableTimer) {
      clearTimeout(this.reconnectNowEnableTimer);
      this.reconnectNowEnableTimer = null;
    }
  }

  /**
   * Clean up when removing from scene
   */
  destroy(): void {
    this.clearTimers();
    super.destroy({ children: true });
  }
}
